import { useI18n } from 'vue-i18n';
import { dynamicTime as dynamicTimeBase } from '../helpers/timeHelper';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';

import { enUS, ru, pl, es, kk, bg } from 'date-fns/locale';

function makeShortLocale(baseLocale, map) {
  return {
    ...baseLocale,
    formatDistance: (token, count, options) => {
      const addSuffix = options?.addSuffix;
      const suffix = addSuffix && map.suffix ? map.suffix : '';

      const tokenMap = {
        lessThanXSeconds: map.xSeconds,
        xSeconds: map.xSeconds,
        halfAMinute: map.xSeconds,
        lessThanXMinutes: map.xMinutes,
        xMinutes: map.xMinutes,
        aboutXHours: map.xHours,
        xHours: map.xHours,
        aboutXDays: map.xDays,
        xDays: map.xDays,
        aboutXMonths: map.xMonths,
        xMonths: map.xMonths,
        aboutXYears: map.xYears,
        xYears: map.xYears,
        overXYears: map.xYears,
        almostXYears: map.xYears,
      };

      const fn = tokenMap[token];
      const value = fn ? fn(count === 0 ? 30 : count) : `${count}`;

      return `${value}${suffix ? ' ' + suffix : ''}`;
    },
  };
}

export const shortLocales = {
  en: makeShortLocale(enUS, {
    xSeconds: c => `${c}s`,
    xMinutes: c => `${c}m`,
    xHours: c => `${c}h`,
    xDays: c => `${c}d`,
    xMonths: c => `${c}mo`,
    xYears: c => `${c}y`,
    suffix: 'ago',
  }),
  ru: makeShortLocale(ru, {
    xSeconds: c => `${c}с`,
    xMinutes: c => `${c}мин`,
    xHours: c => `${c}ч`,
    xDays: c => `${c}д`,
    xMonths: c => `${c}мес`,
    xYears: c => `${c}г`,
    suffix: 'назад',
  }),
  pl: makeShortLocale(pl, {
    xSeconds: c => `${c}s`,
    xMinutes: c => `${c}min`,
    xHours: c => `${c}h`,
    xDays: c => `${c}d`,
    xMonths: c => `${c}mies`,
    xYears: c => `${c}r`,
    suffix: 'temu',
  }),
  es: makeShortLocale(es, {
    xSeconds: c => `${c}s`,
    xMinutes: c => `${c}min`,
    xHours: c => `${c}h`,
    xDays: c => `${c}d`,
    xMonths: c => `${c}mes`,
    xYears: c => `${c}año`,
    suffix: 'atrás',
  }),
  kk: makeShortLocale(kk, {
    xSeconds: c => `${c}с`,
    xMinutes: c => `${c}мин`,
    xHours: c => `${c}сағ`,
    xDays: c => `${c}күн`,
    xMonths: c => `${c}ай`,
    xYears: c => `${c}ж`,
    suffix: 'бұрын',
  }),
  bg: makeShortLocale(bg, {
    xSeconds: c => `${c}сек`,
    xMinutes: c => `${c}мин`,
    xHours: c => `${c}ч`,
    xDays: c => `${c}д`,
    xMonths: c => `${c}мес`,
    xYears: c => `${c}г`,
    suffix: 'преди',
  }),
};

export function useLocalizedDynamicTime() {
  const { locale } = useI18n();

  const dynamicTime = timestamp => dynamicTimeBase(timestamp, locale.value);

  const shortDynamicTime = unixSeconds => {
    if (!unixSeconds) return '';
    const lang = locale.value.split('-')[0];
    const loc = shortLocales[lang] || shortLocales.en;

    return formatDistanceToNow(fromUnixTime(unixSeconds), {
      locale: loc,
      addSuffix: true,
    });
  };

  return {
    dynamicTime,
    shortDynamicTime,
  };
}
